import React, { useState, useEffect, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { fetchChapters, fetchChapterContent, syncChapterContent } from '../../api/adminApi';
import DataList from './DataList';
import DataCard from './DataCard';
import Pagination from '../common/Pagination';

const ITEMS_PER_PAGE = 10;

// Map content_type values to { color, bg } for the badge
const CONTENT_TYPE_STYLE = {
  video: { color: 'var(--accent-blue)',   bg: 'rgba(59,130,246,0.12)' },
  quiz:  { color: 'var(--accent-orange)', bg: 'rgba(249,115,22,0.12)' },
  text:  { color: 'var(--accent-green)',  bg: 'rgba(132,204,22,0.12)' },
};

const ChaptersSection = () => {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const [expandedId, setExpandedId] = useState(null);
  const [nestedData, setNestedData] = useState({});

  // Per-chapter content-sync state, keyed by chapter id:
  // { syncing: boolean, result?: { success: boolean, message: string } }
  const [syncState, setSyncState] = useState({});

  const loadChapters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchChapters(currentPage, ITEMS_PER_PAGE, searchTerm);
      setChapters(data.chapters || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Error loading chapters:', err);
      setError('Failed to load chapters. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadChapters();
  }, [loadChapters]);

  const handleToggle = useCallback(async (chapter) => {
    if (expandedId === chapter.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(chapter.id);

    if (nestedData[chapter.id]) return;

    setNestedData((prev) => ({
      ...prev,
      [chapter.id]: { loading: true, content: [] },
    }));

    try {
      const data = await fetchChapterContent(chapter.id);
      setNestedData((prev) => ({
        ...prev,
        [chapter.id]: { loading: false, content: data.content || [] },
      }));
    } catch (err) {
      console.error(`Error loading content for chapter ${chapter.id}:`, err);
      setNestedData((prev) => ({
        ...prev,
        [chapter.id]: { loading: false, content: [] },
      }));
    }
  }, [expandedId, nestedData]);

  const handleSyncContent = useCallback(async (chapter) => {
    setSyncState((prev) => ({
      ...prev,
      [chapter.id]: { syncing: true },
    }));

    try {
      const { summary } = await syncChapterContent(chapter.id);
      const message =
        `${summary.chapters} chapter(s) · ${summary.created} created · ` +
        `${summary.updated} updated · ${summary.unchanged} unchanged · ` +
        `${summary.relationships} link(s)` +
        (summary.failed ? ` · ${summary.failed} failed` : '');

      setSyncState((prev) => ({
        ...prev,
        [chapter.id]: { syncing: false, result: { success: true, message } },
      }));

      // The synced contents are now stale in both caches — drop the nested rows
      // so the next expand refetches, and reload the list for fresh item counts.
      setNestedData((prev) => {
        const next = { ...prev };
        delete next[chapter.id];
        return next;
      });
      await loadChapters();
    } catch (err) {
      console.error(`Error syncing content for chapter ${chapter.id}:`, err);
      const message =
        err.response?.data?.message || 'Sync failed. Please try again.';
      setSyncState((prev) => ({
        ...prev,
        [chapter.id]: { syncing: false, result: { success: false, message } },
      }));
    }
  }, [loadChapters]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedId(null);
  };

  return (
    <div>
      {error && (
        <div className="data-error-banner">
          <i className="bi bi-exclamation-triangle-fill" />
          {error}
        </div>
      )}

      <DataList
        searchPlaceholder="Search chapters…"
        searchValue={searchTerm}
        onSearch={handleSearch}
        loading={loading}
        empty={!loading && chapters.length === 0}
        emptyMessage={searchTerm ? 'No chapters match your search.' : 'No chapters found.'}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        }
      >
        {chapters.map((chapter) => {
          const nested = nestedData[chapter.id];
          const isExpanded = expandedId === chapter.id;
          const sync = syncState[chapter.id];

          return (
            <DataCard
              key={chapter.id}
              title={chapter.name}
              subtitle={chapter.course?.title ? `Course: ${chapter.course.title}` : undefined}
              badge={`${chapter._count?.contents ?? 0} Items`}
              accentColor="var(--accent-orange)"
              expanded={isExpanded}
              onToggle={() => handleToggle(chapter)}
              headerActions={
                <>
                  <button
                    type="button"
                    className="data-card-action-button"
                    onClick={() => handleSyncContent(chapter)}
                    disabled={sync?.syncing}
                    title="Sync this chapter and its sub-chapters' content from Testpress"
                  >
                    <i
                      className={`bi bi-arrow-repeat${sync?.syncing ? ' spin' : ''}`}
                    />
                    {sync?.syncing ? 'Syncing…' : 'Sync Content'}
                  </button>
                  {sync?.result && (
                    <span
                      className={`data-card-action-result data-card-action-result--${
                        sync.result.success ? 'success' : 'error'
                      }`}
                    >
                      {sync.result.message}
                    </span>
                  )}
                </>
              }
            >
              {nested?.loading ? (
                <div className="data-nested-skeleton">
                  <Skeleton count={3} height={36} borderRadius={6} />
                </div>
              ) : nested?.content?.length === 0 ? (
                <div className="data-nested-empty">No content items in this chapter.</div>
              ) : (
                <div className="data-nested-table-wrap">
                  <table className="data-nested-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Title</th>
                        <th>Type</th>
                        <th>Attempts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(nested?.content || []).map((item, idx) => {
                        const typeStyle =
                          CONTENT_TYPE_STYLE[item.content_type] || {
                            color: 'var(--text-muted)',
                            bg: 'rgba(100,116,139,0.12)',
                          };
                        return (
                          <tr key={item.id}>
                            <td style={{ color: 'var(--text-muted)', width: 32 }}>
                              {idx + 1}
                            </td>
                            <td>{item.title}</td>
                            <td>
                              <span
                                className="data-status-badge"
                                style={{
                                  color: typeStyle.color,
                                  backgroundColor: typeStyle.bg,
                                  textTransform: 'capitalize',
                                }}
                              >
                                {item.content_type || 'unknown'}
                              </span>
                            </td>
                            <td>
                              <span className="data-card-badge" style={{ fontSize: '0.7rem' }}>
                                {item._count?.attempts ?? 0}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </DataCard>
          );
        })}
      </DataList>
    </div>
  );
};

export default ChaptersSection;
